import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import { customerService, fileService } from '../services/apiService';
import api from '../services/api';
import { Search, Plus, User, Upload, X, Image as ImageIcon, FileText, Video, Trash2, ArrowUpDown, EyeOff, Eye, SlidersHorizontal, Diamond, Camera } from 'lucide-react';
import { Input } from './ui/input';
import { PhoneInput, normalizePhone } from './ui/phone-input';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from './ui/dialog';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from './ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import OrderItem from './orders/OrderItem';
import GlobalNavigationMenu from './GlobalNavigationMenu';

const Sidebar = ({ isMobile = false }) => {
  const { orders, selectedOrder, setSelectedOrder, selectOrder, searchQuery, setSearchQuery, sortBy, setSortBy, hideTestOrders, setHideTestOrders, createOrder, pinnedOrderIds, pinOrder, unpinOrder, unreadCounts, customFilters, activeFilter, setActiveFilter, createFilter, deleteFilter, addOrderToFilter, removeOrderFromFilter } = useChat();
  const { currentUser, logout, canAccess } = useAuth();
  const { toast } = useToast();
  // FIX: useLocation was imported but never instantiated — the fromEstimate
  // pre-fill effect at ~line 228 was silently dereferencing window.location
  // (which has no .state), so Convert-to-Order never opened the dialog.
  const location = useLocation();
  const [showNewFilterInput, setShowNewFilterInput] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isFromEstimate, setIsFromEstimate] = useState(false);
  const [isFromRepair, setIsFromRepair] = useState(false);
  const [repairEstimateId, setRepairEstimateId] = useState(null);
  const [convertLeadId, setConvertLeadId] = useState(null);
  const [convertSessionId, setConvertSessionId] = useState(null);
  const [primaryImage, setPrimaryImage] = useState(null);
  const [estimateImages, setEstimateImages] = useState([]);
  const [uploadingEstimate, setUploadingEstimate] = useState(false);

  // Estimate linking
  const [customerEstimates, setCustomerEstimates] = useState([]);
  const [selectedEstimateIds, setSelectedEstimateIds] = useState([]);
  const [lockedEstimateId, setLockedEstimateId] = useState(null); // Lock selection when coming from "Create Order" on estimate

  // Catalog data from API
  const [mainGroupsFromCatalog, setMainGroupsFromCatalog] = useState([]);
  const [productsFromCatalog, setProductsFromCatalog] = useState([]);
  const [goldTypesFromCatalog, setGoldTypesFromCatalog] = useState([]);
  const [todayGoldPrices, setTodayGoldPrices] = useState(null);

  // Advanced filter state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    ordersWithMe: false, orderType: '', mainGroup: '', productName: '',
    customerName: '', currentStage: '', assignedWorker: '',
    deliveryFrom: '', deliveryTo: '', hasUnread: false,
    delayedOrders: false, dueIn7Days: false, materialRequestPending: false,
  });
  const [pendingMaterialOrderIds, setPendingMaterialOrderIds] = useState([]);

  const [newOrderForm, setNewOrderForm] = useState({
    orderNumber: '',
    orderType: '',
    mainGroup: '',
    title: '',
    customerPhone: '',
    customerName: '',
    customerAddress: '',
    productWeight: '',
    metalType: '',
    karatPurity: '',
    stoneDetails: '',
    priceAmount: '',
    deliveryDate: '',
    specialInstructions: '',
    goldRateType: 'fixed',
    goldRatePerGram: '',
    hallmarkingNeeded: false,
    stonePricingDetails: '',
    gia: false,
    igi: false,
    tds: false,
    materialsInvolved: { gold: false, diamond: false, stone: false },
  });

  // Solitaires state
  const [solitaires, setSolitaires] = useState([]);
  const [currentSolitaire, setCurrentSolitaire] = useState({
    shape: '',
    sizeFrom: '',
    sizeTo: '',
    color: '',
    colorGrades: [],    // Array for multiple selection (e.g., ['D', 'E', 'F'])
    clarities: [],      // Array for multiple selection (e.g., ['VVS1', 'VVS2'])
    cut: '',
    polish: '',
    symmetry: '',
    fluorescences: [],  // Array for multiple selection
    labs: [],           // Array for multiple selection (certificates)
    notes: '',
    estimateAmount: '', // Estimate amount given to customer
    source: 'in_stock', // Solitaire availability: 'in_stock' or 'to_source'
  });

  // Solitaire options
  const diamondShapes = ['Round', 'Princess', 'Cushion', 'Oval', 'Emerald', 'Pear', 'Marquise', 'Radiant', 'Asscher', 'Heart', 'Sq. Emerald', 'Trilliant', 'Old European'];
  const colorTypes = ['White', 'Fancy'];
  const colorGrades = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'];
  const clarityGrades = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'I1', 'I2'];
  const cutGrades = ['Ideal', 'Excellent', 'Very Good', 'Good', 'Fair'];
  const polishGrades = ['Excellent', 'Very Good', 'Good', 'Fair'];
  const symmetryGrades = ['Ideal', 'Excellent', 'Very Good', 'Good', 'Fair'];
  const fluorescenceGrades = ['None', 'Very Slight', 'Faint', 'Slight', 'Medium', 'Strong'];
  const labOptions = ['GIA', 'IGI', 'HRD', 'AGS', 'GIA DOR', 'None'];

  // Add solitaire to list
  const handleAddSolitaire = () => {
    if (!currentSolitaire.shape) {
      toast({ title: 'Please select a shape', variant: 'destructive' });
      return;
    }
    setSolitaires([...solitaires, { ...currentSolitaire, id: Date.now() }]);
    setNewOrderForm((f) => ({
      ...f,
      materialsInvolved: { ...f.materialsInvolved, diamond: true },
    }));
    setCurrentSolitaire({
      shape: '',
      sizeFrom: '',
      sizeTo: '',
      color: '',
      colorGrades: [],  // Changed to array for multiple selection
      clarities: [],    // Changed to array for multiple selection
      cut: '',
      polish: '',
      symmetry: '',
      fluorescences: [], // Changed to array for multiple selection
      labs: [],         // Changed to array for multiple selection
      notes: '',
      estimateAmount: '', // Reset estimate amount
      source: 'in_stock', // Reset to in stock
    });
    toast({ title: 'Solitaire added' });
  };

  // Remove solitaire from list
  const handleRemoveSolitaire = (id) => {
    setSolitaires(solitaires.filter(s => s.id !== id));
  };

  // Toggle selection for multi-select fields
  const toggleSelection = (field, value) => {
    const currentValues = currentSolitaire[field] || [];
    if (currentValues.includes(value)) {
      setCurrentSolitaire({
        ...currentSolitaire,
        [field]: currentValues.filter(v => v !== value)
      });
    } else {
      setCurrentSolitaire({
        ...currentSolitaire,
        [field]: [...currentValues, value]
      });
    }
  };

  // Predefined options
  const orderTypes = ['Customer Order', 'Stock Order', 'Misc Repair', 'Customer Repair'];
  const mainGroups = mainGroupsFromCatalog.length > 0 ? mainGroupsFromCatalog : ['Gold', 'Silver', 'Platinum', 'Diamond', 'Polki', 'Kundan', 'Repair', 'Miscellaneous Order'];
  const productNames = productsFromCatalog.length > 0 ? productsFromCatalog : ['Necklace', 'Rings', 'Bangles', 'Earrings', 'Bracelets', 'Pendants', 'Chains'];
  const metalTypes = ['Gold', 'Silver', 'Platinum', 'White Gold', 'Rose Gold'];
  const karatOptions = goldTypesFromCatalog.length > 0 ? goldTypesFromCatalog : ['18K', '22K', '24K', '14K', '925 Silver', '950 Platinum'];
  const orderStatuses = ['Pending', 'In Progress', 'Ready for Delivery', 'Completed', 'Cancelled'];

  // Load catalog data from API
  React.useEffect(() => {
    const loadCatalogData = async () => {
      try {
        const api = (await import('../services/api')).default;
        const [mainGroupsRes, productsRes, goldTypesRes, todayPriceRes] = await Promise.all([
          api.get('/catalog/main-groups'),
          api.get('/catalog/products'),
          api.get('/catalog/gold-types'),
          api.get('/gold-prices/today'),
        ]);
        setMainGroupsFromCatalog(mainGroupsRes.data.map(item => item.name));
        setProductsFromCatalog(productsRes.data.map(item => item.name));
        setGoldTypesFromCatalog(goldTypesRes.data.map(item => item.name));
        if (todayPriceRes.data.exists) {
          setTodayGoldPrices(todayPriceRes.data.prices);
        }
      } catch (error) {
        console.error('Error loading catalog:', error);
      }
    };
    loadCatalogData();
  }, []);

  // Pre-fill order form when navigating from Estimates page
  useEffect(() => {
    if (location.state?.fromEstimate && location.state?.estimateData) {
      const est = location.state.estimateData;
      setIsFromEstimate(true);
      setNewOrderForm(prev => ({
        ...prev,
        orderType: 'Customer Order',
        customerPhone: est.customerPhone || '',
        customerName: est.customerName || '',
        mainGroup: est.mainGroup || '',
        title: est.title || '',
        productWeight: est.productWeight || '',
        karatPurity: est.karatPurity || '',
        goldRatePerGram: est.goldRatePerGram || '',
        goldRateType: est.goldRateType || 'fixed',
        stoneDetails: est.stoneDetails || '',
        priceAmount: est.priceAmount || '',
        materialsInvolved: est.materialsInvolved || { gold: false, diamond: false, stone: false },
      }));

      // Link the estimate and lock it
      if (est.linkedEstimateId) {
        setSelectedEstimateIds([est.linkedEstimateId]);
        setLockedEstimateId(est.linkedEstimateId);
      }
      // Trace link back to the originating lead/session
      setConvertLeadId(location.state?.leadId || null);
      setConvertSessionId(location.state?.sessionId || null);

      // Auto-search customer and load estimates
      if (est.customerPhone && est.customerPhone.length >= 10) {
        (async () => {
          try {
            const customers = await customerService.searchByPhone(est.customerPhone);
            if (customers.length > 0) {
              setExistingCustomers(customers);
              setIsNewCustomer(false);
              setSelectedCustomerId(customers[0].id);
            } else {
              setIsNewCustomer(true);
            }
            // Also load customer estimates so the "Link Existing Estimate" section shows
            const estimatesRes = await api.get(`/estimates?phone=${est.customerPhone}`);
            setCustomerEstimates(estimatesRes.data || []);
          } catch (err) {
            console.error('Error fetching customer:', err);
          }
        })();
      }

      // Open the create order dialog
      setIsCreateDialogOpen(true);

      // Clear the navigation state so refreshing doesn't re-trigger
      window.history.replaceState({}, document.title);

      toast({
        title: 'Order from Estimate',
        description: `Pre-filled from estimate ${est.estimateNumber || ''}. Review and create the order.`,
      });
    }

    // ── Repair lead → Customer Repair order ─────────────────────────────────
    if (location.state?.fromRepairEstimate && location.state?.repairData) {
      const rep = location.state.repairData;
      setIsFromRepair(true);
      setRepairEstimateId(rep.repairEstimateId || null);
      setConvertLeadId(location.state?.leadId || null);
      setConvertSessionId(location.state?.sessionId || null);
      setNewOrderForm(prev => ({
        ...prev,
        orderType: 'Customer Repair',
        customerPhone: rep.customerPhone || '',
        customerName: rep.customerName || '',
        mainGroup: 'Repair',
        title: rep.title || '',
        productWeight: rep.productWeight || '',
        priceAmount: rep.priceAmount || '',
        specialInstructions: rep.specialInstructions || '',
        materialsInvolved: rep.materialsInvolved || { gold: false, diamond: false, stone: false },
      }));

      if (rep.customerPhone && rep.customerPhone.length >= 10) {
        (async () => {
          try {
            const customers = await customerService.searchByPhone(rep.customerPhone);
            if (customers.length > 0) {
              setExistingCustomers(customers);
              setIsNewCustomer(false);
              setSelectedCustomerId(customers[0].id);
            } else {
              setIsNewCustomer(true);
            }
          } catch (err) { console.error('Error fetching customer:', err); }
        })();
      }

      setIsCreateDialogOpen(true);
      window.history.replaceState({}, document.title);
      toast({
        title: 'Repair Order from Estimate',
        description: `Pre-filled from repair estimate ${rep.repairEstimateNumber || ''}. Review and create the order.`,
      });
    }
  }, [location.state]);

  // Handle phone number change and search for existing customers
  const handlePhoneChange = async (phone) => {
    // Functional update so we never clobber the latest phone the user is typing.
    setNewOrderForm(prev => ({ ...prev, customerPhone: phone }));

    if (phone.length >= 10) {
      try {
        const customers = await customerService.searchByPhone(phone);
        if (customers.length > 0) {
          setExistingCustomers(customers);
          setIsNewCustomer(false);
          // Auto-select first customer if only one exists.
          // NOTE: do NOT write customerPhone back here — the async result is stale
          // relative to what the user may have typed since; only set name/address.
          if (customers.length === 1) {
            setSelectedCustomerId(customers[0].id);
            setNewOrderForm(prev => ({
              ...prev,
              customerName: customers[0].name,
              customerAddress: customers[0].address || '',
            }));
          }
        } else {
          setExistingCustomers([]);
          setIsNewCustomer(true);
          setNewOrderForm(prev => ({
            ...prev,
            customerName: '',
            customerAddress: '',
          }));
        }

        // Fetch customer estimates (exclude drafts, show confirmed and final only)
        const estimatesRes = await api.get(`/estimates?phone=${phone}&exclude_drafts=true`);
        setCustomerEstimates(estimatesRes.data || []);
      } catch (error) {
        console.error('Error searching customers:', error);
        setCustomerEstimates([]);
      }
    } else {
      setExistingCustomers([]);
      setIsNewCustomer(false);
      setSelectedCustomerId('');
      setCustomerEstimates([]);
    }
  };

  // Handle customer selection from dropdown
  const handleCustomerSelect = (customerId) => {
    setSelectedCustomerId(customerId);
    const customer = existingCustomers.find(c => c.id === customerId);
    if (customer) {
      setNewOrderForm(prev => ({
        ...prev,
        customerName: customer.name,
        customerAddress: customer.address || '',
      }));
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    try {
      const uploadPromises = files.map(file => fileService.uploadFile(file, (p) => setUploadProgress(p)));
      const uploadResults = await Promise.all(uploadPromises);

      const newFiles = uploadResults.map((result, index) => ({
        url: result.url,
        name: files[index].name,
        type: result.type,
      }));

      setUploadedFiles([...uploadedFiles, ...newFiles]);

      toast({
        title: 'Files uploaded',
        description: `${files.length} file(s) uploaded successfully`,
      });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: typeof error?.message === 'string' && error.message ? error.message : 'Failed to upload files',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      event.target.value = '';
    }
  };

  // Remove uploaded file
  const removeFile = (index) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  // Handle estimate images upload
  const handleEstimateImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingEstimate(true);
    try {
      const uploadPromises = files.map(file => fileService.uploadFile(file));
      const uploadResults = await Promise.all(uploadPromises);

      const newImages = uploadResults.map((result, index) => ({
        url: result.url,
        name: files[index].name,
        type: result.type,
      }));

      setEstimateImages([...estimateImages, ...newImages]);

      toast({
        title: 'Estimate images uploaded',
        description: `${files.length} image(s) uploaded successfully`,
      });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload estimate images',
        variant: 'destructive',
      });
    } finally {
      setUploadingEstimate(false);
      event.target.value = '';
    }
  };

  // Remove estimate image
  const removeEstimateImage = (index) => {
    setEstimateImages(estimateImages.filter((_, i) => i !== index));
  };

  const handleCreateOrder = async () => {
    if (!newOrderForm.orderNumber || !newOrderForm.orderType || !newOrderForm.mainGroup || !newOrderForm.title) {
      toast({
        title: 'Error',
        description: 'Please fill in Order Number, Order Type, Main Group, and Product Name',
        variant: 'destructive',
      });
      return;
    }

    if (!newOrderForm.customerPhone || !newOrderForm.customerName) {
      toast({
        title: 'Error',
        description: 'Please enter customer phone number and name',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Save new customer if this is a new customer
      if (isNewCustomer || selectedCustomerId === 'new') {
        await customerService.createCustomer(
          newOrderForm.customerName,
          newOrderForm.customerPhone,
          newOrderForm.customerAddress
        );
      }

      const orderData = {
        order_number: newOrderForm.orderNumber,
        order_type: newOrderForm.orderType,
        main_group: newOrderForm.mainGroup,
        title: newOrderForm.title,
        customer_name: newOrderForm.customerName || null,
        customer_phone: newOrderForm.customerPhone || null,
        customer_address: newOrderForm.customerAddress || null,
        linked_estimate_ids: selectedEstimateIds,  // NEW: Include linked estimates
        repair_estimate_id: repairEstimateId || null,  // Customer Repair: confirmed ledger link
        lead_id: convertLeadId || null,  // trace link → originating lead
        session_id: convertSessionId || null,  // trace link → originating session
        quantity: null,
        product_weight: newOrderForm.productWeight ? parseFloat(newOrderForm.productWeight) : null,
        metal_type: newOrderForm.metalType || null,
        karat_purity: newOrderForm.karatPurity || null,
        stone_details: newOrderForm.stoneDetails || null,
        price_amount: (newOrderForm.productWeight && newOrderForm.goldRatePerGram)
          ? Math.round(parseFloat(newOrderForm.productWeight) * parseFloat(newOrderForm.goldRatePerGram))
          : (newOrderForm.priceAmount ? parseFloat(newOrderForm.priceAmount) : null),
        advance_payment: null,
        delivery_date: newOrderForm.deliveryDate || null,
        special_instructions: newOrderForm.specialInstructions || null,
        uploaded_files: uploadedFiles.map(f => f.url),
        estimate_images: estimateImages.map(img => img.url),
        primary_image: primaryImage,
        order_status: 'Pending',
        gold_rate_type: newOrderForm.goldRateType || null,
        gold_rate_per_gram: newOrderForm.goldRatePerGram ? parseFloat(newOrderForm.goldRatePerGram) : null,
        hallmarking_needed: newOrderForm.hallmarkingNeeded,
        stone_pricing_details: newOrderForm.stonePricingDetails || null,
        materials_involved: Object.entries(newOrderForm.materialsInvolved || {})
          .filter(([, v]) => v)
          .map(([k]) => k),
        // Solitaires data
        solitaires: solitaires.length > 0 ? solitaires.map(s => ({
          shape: s.shape,
          size_from: s.sizeFrom ? parseFloat(s.sizeFrom) : null,
          size_to: s.sizeTo ? parseFloat(s.sizeTo) : null,
          color: s.color,
          color_grades: s.colorGrades || [],    // Array
          clarities: s.clarities || [],          // Array
          cut: s.cut,
          polish: s.polish,
          symmetry: s.symmetry,
          fluorescences: s.fluorescences || [],  // Array
          labs: s.labs || [],                    // Array
          notes: s.notes,
          estimate_amount: s.estimateAmount ? parseFloat(s.estimateAmount) : null,
          source: s.source || 'in_stock',
        })) : [],
      };

      const createdOrder = await createOrder(orderData);
      // Repair lead → link the confirmed repair-estimate ledger to the new order
      // (feeds net into price + repair closure, fires dept feasibility via materials).
      if (repairEstimateId && createdOrder?.id) {
        try {
          await api.post(`/repair-estimates/${repairEstimateId}/attach-order`, { order_id: createdOrder.id });
        } catch (e) { console.error('Failed to attach repair estimate:', e); }
      }
      // Mark the originating lead as converted (sets linked_order_id → enables the
      // Session→Order trace link and blocks accidental double-conversion).
      if (convertLeadId && createdOrder?.id) {
        try {
          await api.patch(`/crm/leads/${convertLeadId}/link-order`, { order_id: createdOrder.id });
        } catch (e) { console.error('Failed to link order to lead:', e); }
      }
      toast({
        title: 'Order created',
        description: `${newOrderForm.orderNumber} has been created${solitaires.length > 0 ? ` with ${solitaires.length} solitaire(s)` : ''}`,
      });
      // Reset form
      setNewOrderForm({
        orderNumber: '',
        orderType: '',
        mainGroup: '',
        title: '',
        customerPhone: '',
        customerName: '',
        customerAddress: '',
        productWeight: '',
        metalType: '',
        karatPurity: '',
        stoneDetails: '',
        priceAmount: '',
        deliveryDate: '',
        specialInstructions: '',
        goldRateType: 'fixed',
        goldRatePerGram: '',
        hallmarkingNeeded: false,
        stonePricingDetails: '',
        materialsInvolved: { gold: false, diamond: false, stone: false },
      });
      setExistingCustomers([]);
      setSelectedCustomerId('');
      setIsNewCustomer(false);
      setUploadedFiles([]);
      setPrimaryImage(null);
      setEstimateImages([]);
      setSolitaires([]);
      setCustomerEstimates([]);  // NEW: Reset estimates
      setSelectedEstimateIds([]);  // NEW: Reset selected estimates
      setLockedEstimateId(null);  // Reset locked estimate
      setIsFromRepair(false);
      setRepairEstimateId(null);
      setConvertLeadId(null);
      setConvertSessionId(null);
      setIsCreateDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create order',
        variant: 'destructive',
      });
    }
  };

  // Compute unique values for filter dropdowns from all orders
  const filterOptions = React.useMemo(() => {
    const types = new Set();
    const groups = new Set();
    const products = new Set();
    const customers = new Set();
    const stages = new Set();
    const workers = new Set();
    orders.forEach(o => {
      if (o.order_type) types.add(o.order_type);
      if (o.main_group) groups.add(o.main_group);
      if (o.title) products.add(o.title);
      if (o.customer_name) customers.add(o.customer_name);
      if (o.current_stage) stages.add(o.current_stage);
      if (o.assigned_worker_name) workers.add(o.assigned_worker_name);
    });
    return {
      orderTypes: [...types].sort(),
      mainGroups: [...groups].sort(),
      productNames: [...products].sort(),
      customerNames: [...customers].sort(),
      stages: [...stages].sort(),
      workers: [...workers].sort(),
    };
  }, [orders]);

  // Fetch pending material request order IDs when filter is toggled
  React.useEffect(() => {
    if (advancedFilters.materialRequestPending) {
      api.get('/material-requests/pending-order-ids')
        .then(res => setPendingMaterialOrderIds(res.data || []))
        .catch(() => setPendingMaterialOrderIds([]));
    }
  }, [advancedFilters.materialRequestPending]);

  // Apply advanced filters
  const advFilteredOrders = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const in7Days = new Date(today);
    in7Days.setDate(in7Days.getDate() + 7);

    return orders.filter(order => {
      if (advancedFilters.ordersWithMe && !order.members?.includes(currentUser?.id) && order.created_by !== currentUser?.id) return false;
      if (advancedFilters.orderType && order.order_type !== advancedFilters.orderType) return false;
      if (advancedFilters.mainGroup && order.main_group !== advancedFilters.mainGroup) return false;
      if (advancedFilters.productName && order.title !== advancedFilters.productName) return false;
      if (advancedFilters.customerName && order.customer_name !== advancedFilters.customerName) return false;
      if (advancedFilters.currentStage && order.current_stage !== advancedFilters.currentStage) return false;
      if (advancedFilters.assignedWorker && order.assigned_worker_name !== advancedFilters.assignedWorker) return false;
      if (advancedFilters.hasUnread && !(order.unreadCount > 0)) return false;

      // Delayed orders: delivery_date is in the past and order is not completed/delivered
      if (advancedFilters.delayedOrders) {
        if (!order.delivery_date) return false;
        const deliveryDate = new Date(order.delivery_date);
        const isOverdue = deliveryDate < today;
        const isActive = !['Completed', 'Delivered', 'Cancelled'].includes(order.order_status);
        if (!(isOverdue && isActive)) return false;
      }

      // Due in next 7 days: delivery_date is between today and 7 days from now
      if (advancedFilters.dueIn7Days) {
        if (!order.delivery_date) return false;
        const deliveryDate = new Date(order.delivery_date);
        if (!(deliveryDate >= today && deliveryDate <= in7Days)) return false;
      }

      if (advancedFilters.deliveryFrom && order.delivery_date) {
        if (order.delivery_date < advancedFilters.deliveryFrom) return false;
      }
      if (advancedFilters.deliveryTo && order.delivery_date) {
        if (order.delivery_date > advancedFilters.deliveryTo) return false;
      }
      if (advancedFilters.deliveryFrom && !order.delivery_date) return false;

      // Material request pending: only show orders with pending/partial material requests
      if (advancedFilters.materialRequestPending && !pendingMaterialOrderIds.includes(order.id)) return false;

      return true;
    });
  }, [orders, advancedFilters, currentUser, pendingMaterialOrderIds]);

  const activeAdvFilterCount = Object.entries(advancedFilters).filter(([k, v]) => {
    if (typeof v === 'boolean') return v;
    return v !== '';
  }).length;

  const clearAdvancedFilters = () => {
    setAdvancedFilters({
      ordersWithMe: false, orderType: '', mainGroup: '', productName: '',
      customerName: '', currentStage: '', assignedWorker: '',
      deliveryFrom: '', deliveryTo: '', hasUnread: false,
      delayedOrders: false, dueIn7Days: false, materialRequestPending: false,
    });
  };

  // Use advFilteredOrders for the list when advanced filters are active
  const displayOrders = activeAdvFilterCount > 0 ? advFilteredOrders : orders;

  return (
    <div className={`${isMobile ? 'w-full flex-1 min-h-0' : 'w-96 h-full'} bg-white border-r border-gray-200 flex flex-col`}>
      {/* Compact Header */}
      <div className="bg-[#075E54] text-white px-3 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 min-w-0">
            <div className="w-9 h-9 bg-emerald-300 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold text-sm truncate">{currentUser?.username}</h2>
              <p className="text-[10px] text-emerald-200">{currentUser?.role}</p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            {/* Sort toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700 w-8 h-8" data-testid="sort-btn">
                  <ArrowUpDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSortBy('recent')} className={sortBy === 'recent' ? 'bg-emerald-50' : ''}>
                  Recent First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('oldest')} className={sortBy === 'oldest' ? 'bg-emerald-50' : ''}>
                  Oldest First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('delivery')} className={sortBy === 'delivery' ? 'bg-emerald-50' : ''}>
                  By Delivery Date
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Advanced Filter toggle */}
            <Button
              variant="ghost"
              size="icon"
              className={`w-8 h-8 relative ${showAdvancedFilters ? 'text-yellow-300' : 'text-white'} hover:bg-emerald-700`}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              title="Advanced filters"
              data-testid="advanced-filter-btn"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {activeAdvFilterCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[9px] font-bold flex items-center justify-center">
                  {activeAdvFilterCount}
                </span>
              )}
            </Button>

            {/* Hide Test Orders toggle */}
            <Button
              variant="ghost"
              size="icon"
              className={`w-8 h-8 ${hideTestOrders ? 'text-yellow-300 hover:bg-emerald-700' : 'text-white hover:bg-emerald-700'}`}
              onClick={() => setHideTestOrders(!hideTestOrders)}
              title={hideTestOrders ? 'Show test orders' : 'Hide test orders'}
              data-testid="hide-test-btn"
            >
              {hideTestOrders ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>

            {/* Create Order "+" button */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'salesman' || currentUser?.role === 'stock_supervisor') && (
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-emerald-700 w-8 h-8"
                onClick={() => { setIsFromEstimate(false); setIsCreateDialogOpen(true); }}
                data-testid="create-order-plus-btn"
              >
                <Plus className="w-5 h-5" />
              </Button>
            )}

            {/* Menu "⋮" — universal GlobalNavigationMenu */}
            <GlobalNavigationMenu />
          </div>
        </div>
      </div>

      {/* Advanced Filter Panel — collapsible */}
      {showAdvancedFilters && (
        <div className="bg-white border-b border-gray-200 px-3 py-3 space-y-2 max-h-[50vh] overflow-y-auto" data-testid="advanced-filter-panel">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Filters</h3>
            {activeAdvFilterCount > 0 && (
              <button onClick={clearAdvancedFilters} className="text-xs text-red-500 font-medium hover:underline" data-testid="clear-filters-btn">
                Clear All ({activeAdvFilterCount})
              </button>
            )}
          </div>

          {/* Orders with me */}
          <label className="flex items-center gap-2 py-1.5 cursor-pointer">
            <input type="checkbox" checked={advancedFilters.ordersWithMe} onChange={(e) => setAdvancedFilters(p => ({ ...p, ordersWithMe: e.target.checked }))} className="w-4 h-4 text-emerald-600 rounded" />
            <span className="text-sm text-gray-700">Orders with me</span>
          </label>

          {/* Has Unread */}
          <label className="flex items-center gap-2 py-1.5 cursor-pointer">
            <input type="checkbox" checked={advancedFilters.hasUnread} onChange={(e) => setAdvancedFilters(p => ({ ...p, hasUnread: e.target.checked }))} className="w-4 h-4 text-emerald-600 rounded" />
            <span className="text-sm text-gray-700">Has unread messages</span>
          </label>

          {/* Delayed Orders */}
          <label className="flex items-center gap-2 py-1.5 cursor-pointer">
            <input type="checkbox" checked={advancedFilters.delayedOrders} onChange={(e) => setAdvancedFilters(p => ({ ...p, delayedOrders: e.target.checked }))} className="w-4 h-4 text-red-500 rounded" />
            <span className="text-sm text-red-600 font-medium">Delayed orders</span>
          </label>

          {/* Due in 7 days */}
          <label className="flex items-center gap-2 py-1.5 cursor-pointer">
            <input type="checkbox" checked={advancedFilters.dueIn7Days} onChange={(e) => setAdvancedFilters(p => ({ ...p, dueIn7Days: e.target.checked }))} className="w-4 h-4 text-orange-500 rounded" />
            <span className="text-sm text-orange-600 font-medium">Due in next 7 days</span>
          </label>

          {/* Material Request Pending */}
          <label className="flex items-center gap-2 py-1.5 cursor-pointer">
            <input type="checkbox" checked={advancedFilters.materialRequestPending} onChange={(e) => setAdvancedFilters(p => ({ ...p, materialRequestPending: e.target.checked }))} className="w-4 h-4 text-purple-500 rounded" data-testid="filter-material-pending" />
            <span className="text-sm text-purple-600 font-medium">Material request pending</span>
          </label>

          {/* Order Type */}
          <div>
            <label className="text-xs text-gray-500 font-medium">Order Type</label>
            <select value={advancedFilters.orderType} onChange={(e) => setAdvancedFilters(p => ({ ...p, orderType: e.target.value }))} className="w-full mt-0.5 h-8 px-2 text-sm border border-gray-300 rounded bg-white">
              <option value="">All Types</option>
              {filterOptions.orderTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Main Group */}
          <div>
            <label className="text-xs text-gray-500 font-medium">Main Group</label>
            <select value={advancedFilters.mainGroup} onChange={(e) => setAdvancedFilters(p => ({ ...p, mainGroup: e.target.value }))} className="w-full mt-0.5 h-8 px-2 text-sm border border-gray-300 rounded bg-white">
              <option value="">All Groups</option>
              {filterOptions.mainGroups.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>

          {/* Product Name */}
          <div>
            <label className="text-xs text-gray-500 font-medium">Product Name</label>
            <select value={advancedFilters.productName} onChange={(e) => setAdvancedFilters(p => ({ ...p, productName: e.target.value }))} className="w-full mt-0.5 h-8 px-2 text-sm border border-gray-300 rounded bg-white">
              <option value="">All Products</option>
              {filterOptions.productNames.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {/* Customer Name */}
          <div>
            <label className="text-xs text-gray-500 font-medium">Customer</label>
            <select value={advancedFilters.customerName} onChange={(e) => setAdvancedFilters(p => ({ ...p, customerName: e.target.value }))} className="w-full mt-0.5 h-8 px-2 text-sm border border-gray-300 rounded bg-white">
              <option value="">All Customers</option>
              {filterOptions.customerNames.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Production Stage */}
          <div>
            <label className="text-xs text-gray-500 font-medium">Production Stage</label>
            <select value={advancedFilters.currentStage} onChange={(e) => setAdvancedFilters(p => ({ ...p, currentStage: e.target.value }))} className="w-full mt-0.5 h-8 px-2 text-sm border border-gray-300 rounded bg-white">
              <option value="">All Stages</option>
              {filterOptions.stages.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>

          {/* Assigned Worker */}
          {filterOptions.workers.length > 0 && (
            <div>
              <label className="text-xs text-gray-500 font-medium">Assigned Worker</label>
              <select value={advancedFilters.assignedWorker} onChange={(e) => setAdvancedFilters(p => ({ ...p, assignedWorker: e.target.value }))} className="w-full mt-0.5 h-8 px-2 text-sm border border-gray-300 rounded bg-white">
                <option value="">All Workers</option>
                {filterOptions.workers.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
          )}

          {/* Delivery Date Range */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500 font-medium">Delivery From</label>
              <input type="date" value={advancedFilters.deliveryFrom} onChange={(e) => setAdvancedFilters(p => ({ ...p, deliveryFrom: e.target.value }))} className="w-full mt-0.5 h-8 px-2 text-sm border border-gray-300 rounded bg-white" />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">Delivery To</label>
              <input type="date" value={advancedFilters.deliveryTo} onChange={(e) => setAdvancedFilters(p => ({ ...p, deliveryTo: e.target.value }))} className="w-full mt-0.5 h-8 px-2 text-sm border border-gray-300 rounded bg-white" />
            </div>
          </div>
        </div>
      )}

      {/* Create Order Dialog (detached from old button, triggered by "+" icon) */}
      {(currentUser?.role === 'admin' || currentUser?.role === 'salesman' || currentUser?.role === 'stock_supervisor') && (
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Order</DialogTitle>
                <DialogDescription>
                  Fill in the order details for your customer
                </DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="details">Order Details</TabsTrigger>
                  <TabsTrigger value="solitaires" className="flex items-center gap-1">
                    <Diamond className="w-4 h-4" />
                    Solitaires {solitaires.length > 0 && `(${solitaires.length})`}
                  </TabsTrigger>
                </TabsList>

                {/* ORDER DETAILS TAB */}
                <TabsContent value="details" className="space-y-6">
                {/* Basic Order Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Order Information</h3>
                  <div className="space-y-2">
                    <Label htmlFor="orderNumber">Order Number *</Label>
                    <Input
                      id="orderNumber"
                      placeholder="e.g., ORD-2024-005"
                      value={newOrderForm.orderNumber}
                      onChange={(e) =>
                        setNewOrderForm({ ...newOrderForm, orderNumber: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Order Type */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Order Type</h3>
                  <div className="space-y-2">
                    <Label htmlFor="orderType">Order Type *</Label>
                    <Select
                      value={newOrderForm.orderType}
                      disabled={isFromEstimate || isFromRepair}
                      onValueChange={(value) =>
                        setNewOrderForm({ ...newOrderForm, orderType: value })
                      }
                    >
                      <SelectTrigger id="orderType">
                        <SelectValue placeholder="Select order type" />
                      </SelectTrigger>
                      <SelectContent>
                        {(isFromRepair ? ['Customer Repair'] : isFromEstimate ? ['Customer Order'] : orderTypes.filter(t => t !== 'Customer Order' && t !== 'Customer Repair')).map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(isFromEstimate || isFromRepair) ? (
                      <p className="text-[11px] text-emerald-600" data-testid="from-estimate-note">Auto-filled from the linked {isFromRepair ? 'repair ' : ''}estimate — details come from the Lead/Session.</p>
                    ) : (
                      <p className="text-[11px] text-gray-400" data-testid="manual-order-note">Customer orders &amp; repairs are created from a Lead/Session (Convert to Order).</p>
                    )}
                  </div>
                </div>

                {/* Product Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Product Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mainGroup">Main Group *</Label>
                      <Select
                        value={newOrderForm.mainGroup}
                        onValueChange={(value) =>
                          setNewOrderForm({ ...newOrderForm, mainGroup: value })
                        }
                      >
                        <SelectTrigger id="mainGroup">
                          <SelectValue placeholder="Select main group" />
                        </SelectTrigger>
                        <SelectContent>
                          {mainGroups.map((group) => (
                            <SelectItem key={group} value={group}>
                              {group}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="title">Product Name *</Label>
                      <Select
                        value={newOrderForm.title}
                        onValueChange={(value) =>
                          setNewOrderForm({ ...newOrderForm, title: value })
                        }
                      >
                        <SelectTrigger id="title">
                          <SelectValue placeholder="Select a product" />
                        </SelectTrigger>
                        <SelectContent>
                          {productNames.map((product) => (
                            <SelectItem key={product} value={product}>
                              {product}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Customer Details */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Customer Details</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="customerPhone">Phone Number *</Label>
                      <PhoneInput
                        value={normalizePhone(newOrderForm.customerPhone)}
                        onChange={val => handlePhoneChange(val)}
                        inputProps={{ id: "customerPhone" }}
                      />
                      {existingCustomers.length > 0 && (
                        <p className="text-xs text-emerald-600">
                          ✓ Found {existingCustomers.length} existing customer(s) with this number
                        </p>
                      )}
                      {isNewCustomer && newOrderForm.customerPhone.length >= 10 && (
                        <p className="text-xs text-blue-600">
                          → New customer - Please enter name below
                        </p>
                      )}
                    </div>

                    {existingCustomers.length > 1 && (
                      <div className="space-y-2">
                        <Label htmlFor="selectCustomer">Select Customer</Label>
                        <Select
                          value={selectedCustomerId}
                          onValueChange={handleCustomerSelect}
                        >
                          <SelectTrigger id="selectCustomer">
                            <SelectValue placeholder="Choose existing customer" />
                          </SelectTrigger>
                          <SelectContent>
                            {existingCustomers.map((customer) => (
                              <SelectItem key={customer.id} value={customer.id}>
                                {customer.name} - {customer.address || 'No address'}
                              </SelectItem>
                            ))}
                            <SelectItem value="new">+ Add New Customer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {(existingCustomers.length <= 1 || selectedCustomerId === 'new' || isNewCustomer) && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="customerName">Customer Name *</Label>
                          <Input
                            id="customerName"
                            placeholder="Enter customer name"
                            value={newOrderForm.customerName}
                            onChange={(e) =>
                              setNewOrderForm({ ...newOrderForm, customerName: e.target.value })
                            }
                            disabled={existingCustomers.length === 1 && selectedCustomerId !== 'new'}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="customerAddress">Address</Label>
                          <Input
                            id="customerAddress"
                            placeholder="Enter customer address"
                            value={newOrderForm.customerAddress}
                            onChange={(e) =>
                              setNewOrderForm({ ...newOrderForm, customerAddress: e.target.value })
                            }
                          />
                        </div>
                      </>
                    )}

                    {existingCustomers.length === 1 && selectedCustomerId && selectedCustomerId !== 'new' && (
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-sm font-medium text-emerald-800">Selected Customer:</p>
                        <p className="text-sm text-emerald-700">{newOrderForm.customerName}</p>
                        {newOrderForm.customerAddress && (
                          <p className="text-xs text-emerald-600">{newOrderForm.customerAddress}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Link Existing Estimates */}
                {customerEstimates.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">📋 Link Existing Estimate {lockedEstimateId ? '' : '(Optional)'}</h3>
                    <div className="space-y-3">
                      {customerEstimates.map((estimate) => {
                        const isLocked = lockedEstimateId && estimate.id === lockedEstimateId;
                        const isDisabled = lockedEstimateId && estimate.id !== lockedEstimateId;
                        return (
                        <div
                          key={estimate.id}
                          className={`p-3 border-2 rounded-lg transition-all ${
                            selectedEstimateIds.includes(estimate.id)
                              ? 'border-blue-500 bg-blue-50'
                              : isDisabled
                                ? 'border-gray-100 bg-gray-50 opacity-50'
                                : 'border-gray-200 hover:border-blue-300 cursor-pointer'
                          } ${isLocked ? 'ring-2 ring-blue-400' : ''} ${!isDisabled ? 'cursor-pointer' : ''}`}
                          onClick={() => {
                            if (isDisabled || isLocked) return;
                            if (selectedEstimateIds.includes(estimate.id)) {
                              setSelectedEstimateIds(selectedEstimateIds.filter(id => id !== estimate.id));
                            } else {
                              setSelectedEstimateIds([...selectedEstimateIds, estimate.id]);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={selectedEstimateIds.includes(estimate.id)}
                                  readOnly
                                  disabled={isDisabled}
                                  className="w-4 h-4"
                                />
                                <p className={`font-semibold text-sm ${isDisabled ? 'text-gray-400' : ''}`}>{estimate.estimate_number}</p>
                                <span className={`px-2 py-0.5 rounded-full text-xs ${
                                  estimate.status === 'final'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {estimate.status}
                                </span>
                                {isLocked && (
                                  <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 font-medium">
                                    Source
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-600 mt-1">
                                {estimate.main_group && `${estimate.main_group} • `}
                                {estimate.product && `${estimate.product} • `}
                                {new Date(estimate.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                              </p>
                              <p className="text-sm font-bold text-blue-600 mt-1">
                                Total: ₹{estimate.total_amount?.toLocaleString('en-IN') || 'N/A'}
                              </p>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                      {selectedEstimateIds.length > 0 && (
                        <p className="text-sm text-blue-600 font-medium">
                          ✓ {selectedEstimateIds.length} estimate(s) selected
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Product Specifications */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Product Specifications</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="productWeight">Weight (grams)</Label>
                      <Input
                        id="productWeight"
                        type="number"
                        step="0.01"
                        placeholder="Enter weight"
                        value={newOrderForm.productWeight}
                        onChange={(e) =>
                          setNewOrderForm({ ...newOrderForm, productWeight: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="metalType">Metal Type</Label>
                      <Select
                        value={newOrderForm.metalType}
                        onValueChange={(value) =>
                          setNewOrderForm((f) => ({
                            ...f,
                            metalType: value,
                            materialsInvolved: { ...f.materialsInvolved, gold: true },
                          }))
                        }
                      >
                        <SelectTrigger id="metalType">
                          <SelectValue placeholder="Select metal" />
                        </SelectTrigger>
                        <SelectContent>
                          {metalTypes.map((metal) => (
                            <SelectItem key={metal} value={metal}>
                              {metal}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="karatPurity">Karat/Purity</Label>
                      <Select
                        value={newOrderForm.karatPurity}
                        onValueChange={(value) => {
                          const rate = todayGoldPrices?.[value];
                          setNewOrderForm({
                            ...newOrderForm,
                            karatPurity: value,
                            goldRatePerGram: rate ? String(rate) : newOrderForm.goldRatePerGram,
                          });
                        }}
                      >
                        <SelectTrigger id="karatPurity">
                          <SelectValue placeholder="Select karat/purity" />
                        </SelectTrigger>
                        <SelectContent>
                          {karatOptions.map((karat) => (
                            <SelectItem key={karat} value={karat}>
                              {karat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="stoneDetails">Stone Details</Label>
                      <Input
                        id="stoneDetails"
                        placeholder="e.g., Diamond, Ruby"
                        value={newOrderForm.stoneDetails}
                        onChange={(e) =>
                          setNewOrderForm({ ...newOrderForm, stoneDetails: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  {/* Materials Involved — drives which departments are auto-notified to confirm stock */}
                  <div className="space-y-2 mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <Label className="text-sm font-semibold text-emerald-800">Materials Involved</Label>
                    <p className="text-[11px] text-emerald-600 -mt-1">Tick what this order uses — the matching department is asked to confirm availability before acceptance.</p>
                    <div className="flex flex-wrap gap-4 pt-1">
                      {[
                        { key: 'gold', label: 'Gold / Metal' },
                        { key: 'diamond', label: 'Diamonds' },
                        { key: 'stone', label: 'Colour Stones' },
                      ].map((m) => (
                        <label key={m.key} className="flex items-center gap-2 cursor-pointer" data-testid={`material-${m.key}-label`}>
                          <input
                            type="checkbox"
                            checked={!!newOrderForm.materialsInvolved[m.key]}
                            onChange={(e) => setNewOrderForm((f) => ({
                              ...f,
                              materialsInvolved: { ...f.materialsInvolved, [m.key]: e.target.checked },
                            }))}
                            className="w-4 h-4 text-emerald-600 rounded"
                            data-testid={`material-${m.key}-checkbox`}
                          />
                          <span className="text-sm text-gray-700">{m.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Gold Rate & Hallmarking */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Gold Rate & Hallmarking</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Gold Rate</Label>
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            checked={newOrderForm.goldRateType === 'fixed'}
                            onChange={() =>
                              setNewOrderForm({ ...newOrderForm, goldRateType: 'fixed' })
                            }
                            className="w-4 h-4 text-emerald-600"
                          />
                          <span className="text-sm">Fixed</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            checked={newOrderForm.goldRateType === 'open'}
                            onChange={() =>
                              setNewOrderForm({ ...newOrderForm, goldRateType: 'open' })
                            }
                            className="w-4 h-4 text-emerald-600"
                          />
                          <span className="text-sm">Open</span>
                        </label>
                      </div>
                    </div>
                    {newOrderForm.goldRateType === 'fixed' && (
                      <div className="space-y-2">
                        <Label htmlFor="goldRatePerGram">Rate per Gram (₹)</Label>
                        <Input
                          id="goldRatePerGram"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Enter rate"
                          value={newOrderForm.goldRatePerGram}
                          onChange={(e) =>
                            setNewOrderForm({ ...newOrderForm, goldRatePerGram: e.target.value })
                          }
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="hallmarkingNeeded"
                      checked={newOrderForm.hallmarkingNeeded}
                      onChange={(e) =>
                        setNewOrderForm({ ...newOrderForm, hallmarkingNeeded: e.target.checked })
                      }
                      className="w-4 h-4 text-emerald-600 rounded"
                    />
                    <Label htmlFor="hallmarkingNeeded" className="cursor-pointer">
                      Hallmarking Needed
                    </Label>
                  </div>
                </div>

                {/* Pricing */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Pricing</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="priceAmount">Total Amount (₹)</Label>
                      <Input
                        id="priceAmount"
                        type="number"
                        readOnly
                        value={
                          newOrderForm.productWeight && newOrderForm.goldRatePerGram
                            ? Math.round(parseFloat(newOrderForm.productWeight) * parseFloat(newOrderForm.goldRatePerGram))
                            : ''
                        }
                        placeholder="Weight × Gold Rate"
                        className="bg-emerald-50 text-emerald-700 font-semibold"
                      />
                      {newOrderForm.productWeight && newOrderForm.goldRatePerGram && (
                        <p className="text-xs text-gray-500">
                          {newOrderForm.productWeight}g × ₹{parseFloat(newOrderForm.goldRatePerGram).toLocaleString('en-IN')}/g
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="stonePricingDetails">Stone Pricing Details</Label>
                      <textarea
                        id="stonePricingDetails"
                        rows="3"
                        placeholder="Enter stone pricing details..."
                        value={newOrderForm.stonePricingDetails}
                        onChange={(e) =>
                          setNewOrderForm({ ...newOrderForm, stonePricingDetails: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Delivery & Status */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Delivery & Status</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="deliveryDate">Delivery Date</Label>
                      <Input
                        id="deliveryDate"
                        type="date"
                        value={newOrderForm.deliveryDate}
                        onChange={(e) =>
                          setNewOrderForm({ ...newOrderForm, deliveryDate: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="specialInstructions">Special Instructions</Label>
                      <textarea
                        id="specialInstructions"
                        rows="3"
                        placeholder="Enter any special instructions..."
                        value={newOrderForm.specialInstructions}
                        onChange={(e) =>
                          setNewOrderForm({ ...newOrderForm, specialInstructions: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    {/* File Upload */}
                    <div className="space-y-2">
                      <Label>Upload Files (Images / Videos / PDF)</Label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-emerald-500 transition-colors">
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <div className="flex flex-wrap gap-2 justify-center">
                            {/* Camera capture — opens camera directly on Android */}
                            <label htmlFor="cameraUpload" className="cursor-pointer">
                              <div className="flex items-center space-x-2 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 transition-colors">
                                <Camera className="w-4 h-4" />
                                <span className="text-sm">Camera</span>
                              </div>
                              <input
                                id="cameraUpload"
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploading}
                              />
                            </label>
                            <label htmlFor="imageUpload" className="cursor-pointer">
                              <div className="flex items-center space-x-2 px-4 py-2 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors">
                                <ImageIcon className="w-4 h-4" />
                                <span className="text-sm">Gallery</span>
                              </div>
                              <input
                                id="imageUpload"
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploading}
                              />
                            </label>
                            <label htmlFor="videoUpload" className="cursor-pointer">
                              <div className="flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors">
                                <Video className="w-4 h-4" />
                                <span className="text-sm">Video</span>
                              </div>
                              <input
                                id="videoUpload"
                                type="file"
                                accept="video/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploading}
                              />
                            </label>
                            <label htmlFor="pdfUpload" className="cursor-pointer">
                              <div className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors">
                                <FileText className="w-4 h-4" />
                                <span className="text-sm">PDF</span>
                              </div>
                              <input
                                id="pdfUpload"
                                type="file"
                                accept="application/pdf"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploading}
                              />
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 text-center">
                            Camera, Gallery, videos, or PDF documents<br/>
                            Multiple files can be selected
                          </p>
                          {uploading && (
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                              <span className="text-sm text-gray-600">Uploading{uploadProgress > 0 ? ` ${uploadProgress}%` : '...'}</span>
                            </div>
                          )}
                        </div>

                        {/* Uploaded Files Preview + Primary Image Selection */}
                        {uploadedFiles.length > 0 && (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm font-medium text-gray-700">
                              Uploaded Files ({uploadedFiles.length})
                              {uploadedFiles.filter(f => f.type === 'image').length > 1 && (
                                <span className="text-xs text-emerald-600 ml-2">Tap an image to set as primary for print</span>
                              )}
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                              {uploadedFiles.map((file, index) => {
                                const isImage = file.type === 'image';
                                const isPrimary = primaryImage === file.url;
                                // Auto-set primary if only one image
                                if (isImage && !primaryImage && uploadedFiles.filter(f => f.type === 'image').length === 1) {
                                  setPrimaryImage(file.url);
                                }
                                return (
                                <div
                                  key={index}
                                  className={`relative border-2 rounded-lg p-2 transition-colors cursor-pointer ${
                                    isPrimary ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-300' : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                  onClick={() => isImage && setPrimaryImage(file.url)}
                                >
                                  {isPrimary && (
                                    <div className="absolute top-1 left-1 z-10 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                                      PRIMARY
                                    </div>
                                  )}
                                  {isImage ? (
                                    <img
                                      src={file.url}
                                      alt={file.name}
                                      className="w-full h-24 object-cover rounded"
                                    />
                                  ) : file.name?.toLowerCase().endsWith('.mp4') ||
                                     file.name?.toLowerCase().endsWith('.mov') ||
                                     file.name?.toLowerCase().endsWith('.avi') ||
                                     file.name?.toLowerCase().endsWith('.webm') ? (
                                    <div className="w-full h-24 bg-gray-100 rounded flex flex-col items-center justify-center">
                                      <Video className="w-8 h-8 text-purple-500" />
                                      <p className="text-xs text-gray-600 mt-1">Video</p>
                                    </div>
                                  ) : (
                                    <div className="w-full h-24 bg-gray-100 rounded flex flex-col items-center justify-center">
                                      <FileText className="w-8 h-8 text-blue-500" />
                                      <p className="text-xs text-gray-600 mt-1">PDF</p>
                                    </div>
                                  )}
                                  <p className="text-xs text-gray-600 mt-1 truncate">
                                    {file.name}
                                  </p>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); removeFile(index); if (isPrimary) setPrimaryImage(null); }}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Certifications - inline */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Certifications</h3>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" id="gia" checked={newOrderForm.gia || false} onChange={(e) => setNewOrderForm({ ...newOrderForm, gia: e.target.checked })} className="w-4 h-4 text-emerald-600" />
                      <Label htmlFor="gia" className="cursor-pointer">GIA</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" id="igi" checked={newOrderForm.igi || false} onChange={(e) => setNewOrderForm({ ...newOrderForm, igi: e.target.checked })} className="w-4 h-4 text-emerald-600" />
                      <Label htmlFor="igi" className="cursor-pointer">IGI</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" id="tds" checked={newOrderForm.tds || false} onChange={(e) => setNewOrderForm({ ...newOrderForm, tds: e.target.checked })} className="w-4 h-4 text-emerald-600" />
                      <Label htmlFor="tds" className="cursor-pointer">TDS</Label>
                    </div>
                  </div>
                </div>
                </TabsContent>


                {/* SOLITAIRES TAB */}
                <TabsContent value="solitaires" className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 border-b pb-2 flex items-center">
                      <Diamond className="w-4 h-4 mr-2" />
                      Add Solitaire Diamond
                    </h3>

                    {/* Shape Selection */}
                    <div className="space-y-2">
                      <Label>Shape *</Label>
                      <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                        {diamondShapes.map((shape) => (
                          <button
                            key={shape}
                            type="button"
                            onClick={() => setCurrentSolitaire({ ...currentSolitaire, shape })}
                            className={`p-2 text-xs border rounded-lg transition-colors ${
                              currentSolitaire.shape === shape
                                ? 'bg-emerald-500 text-white border-emerald-500'
                                : 'bg-white hover:bg-gray-50 border-gray-200'
                            }`}
                          >
                            {shape}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Size Range */}
                    <div className="space-y-2">
                      <Label>Size (Carat)</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="From (e.g., 0.30)"
                          value={currentSolitaire.sizeFrom}
                          onChange={(e) => setCurrentSolitaire({ ...currentSolitaire, sizeFrom: e.target.value })}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="To (e.g., 0.50)"
                          value={currentSolitaire.sizeTo}
                          onChange={(e) => setCurrentSolitaire({ ...currentSolitaire, sizeTo: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* Color */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Color Type</Label>
                        <Select
                          value={currentSolitaire.color}
                          onValueChange={(value) => setCurrentSolitaire({ ...currentSolitaire, color: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {colorTypes.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Color Grade <span className="text-gray-400 text-xs">(multi-select)</span></Label>
                        <div className="flex flex-wrap gap-1">
                          {colorGrades.map((grade) => (
                            <button
                              key={grade}
                              type="button"
                              onClick={() => toggleSelection('colorGrades', grade)}
                              className={`px-2 py-1 text-xs border rounded transition-colors ${
                                currentSolitaire.colorGrades?.includes(grade)
                                  ? 'bg-emerald-500 text-white border-emerald-500'
                                  : 'bg-white hover:bg-gray-50'
                              }`}
                            >
                              {grade}
                            </button>
                          ))}
                        </div>
                        {currentSolitaire.colorGrades?.length > 0 && (
                          <p className="text-xs text-emerald-600 font-medium">
                            Selected: {currentSolitaire.colorGrades.join(', ')}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Clarity */}
                    <div className="space-y-2">
                      <Label>Clarity <span className="text-gray-400 text-xs">(multi-select)</span></Label>
                      <div className="flex flex-wrap gap-1">
                        {clarityGrades.map((grade) => (
                          <button
                            key={grade}
                            type="button"
                            onClick={() => toggleSelection('clarities', grade)}
                            className={`px-2 py-1 text-xs border rounded transition-colors ${
                              currentSolitaire.clarities?.includes(grade)
                                ? 'bg-emerald-500 text-white border-emerald-500'
                                : 'bg-white hover:bg-gray-50'
                            }`}
                          >
                            {grade}
                          </button>
                        ))}
                      </div>
                      {currentSolitaire.clarities?.length > 0 && (
                        <p className="text-xs text-emerald-600 font-medium">
                          Selected: {currentSolitaire.clarities.join(', ')}
                        </p>
                      )}
                    </div>

                    {/* Cut, Polish, Symmetry */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Cut</Label>
                        <Select
                          value={currentSolitaire.cut}
                          onValueChange={(value) => setCurrentSolitaire({ ...currentSolitaire, cut: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {cutGrades.map((g) => (
                              <SelectItem key={g} value={g}>{g}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Polish</Label>
                        <Select
                          value={currentSolitaire.polish}
                          onValueChange={(value) => setCurrentSolitaire({ ...currentSolitaire, polish: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {polishGrades.map((g) => (
                              <SelectItem key={g} value={g}>{g}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Symmetry</Label>
                        <Select
                          value={currentSolitaire.symmetry}
                          onValueChange={(value) => setCurrentSolitaire({ ...currentSolitaire, symmetry: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {symmetryGrades.map((g) => (
                              <SelectItem key={g} value={g}>{g}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Fluorescence - Multi-select */}
                    <div className="space-y-2">
                      <Label>Fluorescence <span className="text-gray-400 text-xs">(multi-select)</span></Label>
                      <div className="flex flex-wrap gap-1">
                        {fluorescenceGrades.map((grade) => (
                          <button
                            key={grade}
                            type="button"
                            onClick={() => toggleSelection('fluorescences', grade)}
                            className={`px-2 py-1 text-xs border rounded transition-colors ${
                              currentSolitaire.fluorescences?.includes(grade)
                                ? 'bg-emerald-500 text-white border-emerald-500'
                                : 'bg-white hover:bg-gray-50'
                            }`}
                          >
                            {grade}
                          </button>
                        ))}
                      </div>
                      {currentSolitaire.fluorescences?.length > 0 && (
                        <p className="text-xs text-emerald-600 font-medium">
                          Selected: {currentSolitaire.fluorescences.join(', ')}
                        </p>
                      )}
                    </div>

                    {/* Grading Lab / Certificate - Multi-select */}
                    <div className="space-y-2">
                      <Label>Grading Lab / Certificate <span className="text-gray-400 text-xs">(multi-select)</span></Label>
                      <div className="flex flex-wrap gap-1">
                        {labOptions.map((lab) => (
                          <button
                            key={lab}
                            type="button"
                            onClick={() => toggleSelection('labs', lab)}
                            className={`px-2 py-1 text-xs border rounded transition-colors ${
                              currentSolitaire.labs?.includes(lab)
                                ? 'bg-emerald-500 text-white border-emerald-500'
                                : 'bg-white hover:bg-gray-50'
                            }`}
                          >
                            {lab}
                          </button>
                        ))}
                      </div>
                      {currentSolitaire.labs?.length > 0 && (
                        <p className="text-xs text-emerald-600 font-medium">
                          Selected: {currentSolitaire.labs.join(', ')}
                        </p>
                      )}
                    </div>

                    {/* Notes */}
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Input
                        placeholder="Additional requirements (e.g., No BGM, Eye Clean)"
                        value={currentSolitaire.notes}
                        onChange={(e) => setCurrentSolitaire({ ...currentSolitaire, notes: e.target.value })}
                      />
                    </div>

                    {/* Estimate Amount */}
                    <div className="space-y-2">
                      <Label>Estimate Amount (₹) <span className="text-gray-400 text-xs">(given to customer)</span></Label>
                      <Input
                        type="number"
                        placeholder="Enter estimate amount"
                        value={currentSolitaire.estimateAmount}
                        onChange={(e) => setCurrentSolitaire({ ...currentSolitaire, estimateAmount: e.target.value })}
                        className="text-lg font-medium"
                      />
                    </div>


                    {/* Solitaire Source */}
                    <div className="space-y-2">
                      <Label className="font-semibold">Solitaire Availability</Label>
                      <div className="flex items-center space-x-6">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={currentSolitaire.source === 'in_stock'}
                            onChange={() => setCurrentSolitaire({ ...currentSolitaire, source: 'in_stock' })}
                            className="w-4 h-4 text-emerald-600"
                          />
                          <span className="text-sm">✅ In Stock</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={currentSolitaire.source === 'to_source'}
                            onChange={() => setCurrentSolitaire({ ...currentSolitaire, source: 'to_source' })}
                            className="w-4 h-4 text-emerald-600"
                          />
                          <span className="text-sm">🔍 To Be Sourced</span>
                        </label>
                      </div>
                    </div>


                    {/* Add Button */}
                    <Button
                      type="button"
                      onClick={handleAddSolitaire}
                      className="w-full bg-blue-500 hover:bg-blue-600"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Solitaire to Order
                    </Button>
                  </div>

                  {/* Added Solitaires List */}
                  {solitaires.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">
                        Added Solitaires ({solitaires.length})
                      </h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {solitaires.map((sol, idx) => (
                          <div key={sol.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <p className="font-medium text-sm">
                                    #{idx + 1} {sol.shape} {sol.sizeFrom && sol.sizeTo ? `${sol.sizeFrom}-${sol.sizeTo}ct` : ''}
                                  </p>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    sol.source === 'in_stock'
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-orange-100 text-orange-700'
                                  }`}>
                                    {sol.source === 'in_stock' ? '✅ Stock' : '🔍 Source'}
                                  </span>
                                </div>
                                {sol.estimateAmount && (
                                  <span className="text-emerald-600 font-semibold text-sm">
                                    ₹{parseFloat(sol.estimateAmount).toLocaleString('en-IN')}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">
                                {[
                                  sol.colorGrades?.length > 0 ? sol.colorGrades.join('') : null,
                                  sol.clarities?.length > 0 ? sol.clarities.join('/') : null,
                                  sol.cut,
                                  sol.labs?.length > 0 ? sol.labs.join('/') : null
                                ].filter(Boolean).join(' | ')}
                              </p>
                              {sol.fluorescences?.length > 0 && (
                                <p className="text-xs text-gray-400">Fluor: {sol.fluorescences.join(', ')}</p>
                              )}
                              {sol.notes && <p className="text-xs text-gray-400 mt-1">{sol.notes}</p>}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveSolitaire(sol.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-full ml-2"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateOrder}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  Create Order
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
      )}

      {/* Orders List — Search bar is inside scrollable area (WhatsApp-style) */}
      <div className="flex-1 overflow-y-auto">
        {/* Collapsible Search — scrolls away with the list */}
        <div className="px-3 py-2 bg-[#f0f2f5]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search orders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white border-gray-300 h-9 text-sm"
              data-testid="search-orders-input"
            />
          </div>
        </div>

        {/* Filter Chips — WhatsApp-style horizontal scroll */}
        <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto no-scrollbar" data-testid="filter-chips">
          {/* All */}
          <button
            onClick={() => setActiveFilter('all')}
            className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all ${
              activeFilter === 'all'
                ? 'bg-[#075E54] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="filter-chip-all"
          >
            All
          </button>
          {/* Unread */}
          <button
            onClick={() => setActiveFilter('unread')}
            className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
              activeFilter === 'unread'
                ? 'bg-[#25D366] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="filter-chip-unread"
          >
            Unread
            {Object.keys(unreadCounts).length > 0 && (
              <span className={`text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 ${
                activeFilter === 'unread' ? 'bg-white/30' : 'bg-[#25D366] text-white'
              }`}>
                {Object.keys(unreadCounts).length}
              </span>
            )}
          </button>
          {/* Custom filters */}
          {customFilters.map((filter) => (
            <button
              key={filter.name}
              onClick={() => setActiveFilter(filter.name)}
              onContextMenu={(e) => {
                e.preventDefault();
                if (window.confirm(`Delete filter "${filter.name}"?`)) {
                  deleteFilter(filter.name);
                }
              }}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                activeFilter === filter.name
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title="Long-press to delete"
              data-testid={`filter-chip-${filter.name}`}
            >
              {filter.name}
              {filter.order_ids?.length > 0 && (
                <span className={`text-[10px] rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 ${
                  activeFilter === filter.name ? 'bg-white/30' : 'bg-violet-100 text-violet-700'
                }`}>
                  {filter.order_ids.length}
                </span>
              )}
            </button>
          ))}
          {/* Add filter "+" */}
          {!showNewFilterInput ? (
            <button
              onClick={() => setShowNewFilterInput(true)}
              className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500"
              data-testid="add-filter-btn"
              title="Create custom filter"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          ) : (
            <form
              className="flex items-center gap-1 flex-shrink-0"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!newFilterName.trim()) return;
                const result = await createFilter(newFilterName.trim());
                if (result === true) {
                  toast({ title: 'Filter created', description: `"${newFilterName.trim()}" added` });
                  setNewFilterName('');
                  setShowNewFilterInput(false);
                } else {
                  toast({ title: 'Error', description: result, variant: 'destructive' });
                }
              }}
            >
              <input
                type="text"
                value={newFilterName}
                onChange={(e) => setNewFilterName(e.target.value)}
                placeholder="Name..."
                className="w-20 h-7 px-2 text-xs border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-emerald-500"
                autoFocus
                data-testid="new-filter-input"
              />
              <button type="submit" className="text-emerald-600 text-xs font-bold px-1">OK</button>
              <button type="button" onClick={() => { setShowNewFilterInput(false); setNewFilterName(''); }} className="text-gray-400 text-xs px-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </form>
          )}
        </div>

        {/* Brokers should not see the orders list */}
        {currentUser?.role === 'broker' ? (
          <div className="p-8 text-center text-gray-500">
            <Diamond className="w-16 h-16 mx-auto mb-4 text-emerald-600" />
            <p className="text-lg font-semibold mb-2">Solitaire Procurement</p>
            <p className="text-sm">Please use the menu to access Solitaire Procurement</p>
          </div>
        ) : displayOrders.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>{activeAdvFilterCount > 0 ? 'No orders match your filters' : 'No orders found'}</p>
            {activeAdvFilterCount > 0 && (
              <button onClick={clearAdvancedFilters} className="text-sm text-emerald-600 mt-2 hover:underline">Clear filters</button>
            )}
          </div>
        ) : (
          displayOrders.map((order) => (
            <DropdownMenu key={order.id}>
              <DropdownMenuTrigger asChild>
                <div onContextMenu={(e) => e.preventDefault()}>
                  <OrderItem
                    order={order}
                    isSelected={selectedOrder?.id === order.id}
                    onClick={() => selectOrder(order)}
                    isPinned={pinnedOrderIds.includes(order.id)}
                    onTogglePin={async (orderId) => {
                      if (pinnedOrderIds.includes(orderId)) {
                        await unpinOrder(orderId);
                      } else {
                        if (pinnedOrderIds.length >= 3) {
                          toast({ title: 'Pin limit reached', description: 'You can pin up to 3 orders. Unpin one first.', variant: 'destructive' });
                          return;
                        }
                        await pinOrder(orderId);
                      }
                    }}
                    customFilters={customFilters}
                  />
                </div>
              </DropdownMenuTrigger>
              {customFilters.length > 0 && (
                <DropdownMenuContent align="end" className="w-48">
                  <p className="px-2 py-1 text-xs text-gray-400 font-medium">Assign to filter</p>
                  {customFilters.map((filter) => {
                    const isInFilter = filter.order_ids?.includes(order.id);
                    return (
                      <DropdownMenuItem
                        key={filter.name}
                        onClick={async () => {
                          if (isInFilter) {
                            await removeOrderFromFilter(filter.name, order.id);
                            toast({ title: `Removed from "${filter.name}"` });
                          } else {
                            await addOrderToFilter(filter.name, order.id);
                            toast({ title: `Added to "${filter.name}"` });
                          }
                        }}
                      >
                        <span className={`w-3 h-3 rounded-sm mr-2 border ${isInFilter ? 'bg-violet-500 border-violet-500' : 'border-gray-300'}`} />
                        {filter.name}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              )}
            </DropdownMenu>
          ))
        )}
      </div>

      {/* ── Change Password modal is now in GlobalNavigationMenu ── */}
    </div>
  );
};

export default Sidebar;
